// frontend/src/pages/Referral.js
import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeCanvas } from 'qrcode.react';
import api from '../api';
import { useNavigate } from 'react-router-dom';

export default function Referral() {
  const [user, setUser] = useState(null);
  const [referralLink, setReferralLink] = useState('');
  const [stats, setStats] = useState({ invited: 0, earned: 0 });
  const [copied, setCopied] = useState(false);
  const qrRef = useRef();
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  useEffect(() => {
    if (!token) {
      navigate('/login_page');
      return;
    }

    const fetchData = async () => {
      try {
        const [profileRes, statsRes] = await Promise.all([
          api.get('/api/users/me', { headers: { Authorization: `Bearer ${token}` } }),
          api.get('/api/referral/stats', { headers: { Authorization: `Bearer ${token}` } })
        ]);

        const userData = profileRes.data.user;
        setUser(userData);
        setReferralLink(`${window.location.origin}/register?ref=${userData.referral_code}`);
        setStats(statsRes.data);
      } catch (err) {
        console.error('خطا در دریافت اطلاعات دعوت:', err);
      }
    };

    fetchData();
  }, [token, navigate]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('کپی نشد:', err);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'دعوت به طلافروشی آنلاین',
          text: 'با کد من ثبت‌نام کن و هدیه بگیر!',
          url: referralLink,
        });
      } catch (err) {
        handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  };

  const downloadQR = () => {
    const canvas = qrRef.current.querySelector('canvas');
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `دعوت-${user.referral_code}.png`;
    a.click();
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-purple-600"></div>
          <p className="text-gray-600 font-medium">در حال بارگذاری...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 px-4 py-8 font-vazir">
        <div className="max-w-md mx-auto">

          {/* دکمه برگشت */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ x: -5 }}
            onClick={() => navigate(-1)}
            className="flex items-center text-indigo-600 font-medium mb-6"
          >
            <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            برگشت
          </motion.button>

          {/* عنوان */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-bold text-center bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-10"
          >
            دعوت از دوستان
          </motion.h1>

          {/* QR Code */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            ref={qrRef}
            className="bg-white rounded-3xl p-8 shadow-xl border border-gray-200 mb-8 text-center"
          >
            <div className="relative inline-block">
              <QRCodeCanvas
                value={referralLink}
                size={180}
                level="H"
                includeMargin={false}
                className="rounded-2xl"
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-purple-600/20 to-transparent pointer-events-none"></div>
            </div>
            <p className="text-sm text-gray-600 mt-4 font-medium">اسکن کنید و دعوت شوید</p>
          </motion.div>

          {/* کد و لینک */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl p-6 shadow-lg border border-gray-200 mb-6"
          >
            <div className="flex justify-between items-center mb-3">
              <span className="text-gray-600 font-medium">کد دعوت شما:</span>
              <span className="text-xl font-bold text-purple-600 font-mono">{user.referral_code}</span>
            </div>
            <div className="text-xs text-gray-500 break-all bg-gray-50 p-3 rounded-xl font-mono">
              {referralLink}
            </div>
            <AnimatePresence>
              {copied && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="text-green-600 text-sm font-medium mt-2 text-center"
                >
                  لینک کپی شد!
                </motion.p>
              )}
            </AnimatePresence>
          </motion.div>

          {/* آمار */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-2 gap-4 mb-8"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-2xl p-5 text-center shadow-lg"
            >
              <div className="text-3xl font-bold">{stats.invited}</div>
              <div className="text-sm font-medium mt-1">دعوت موفق</div>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-2xl p-5 text-center shadow-lg"
            >
              <div className="text-3xl font-bold">{parseFloat(stats.earned).toFixed(3)}</div>
              <div className="text-sm font-medium mt-1">گرم طلا هدیه</div>
            </motion.div>
          </motion.div>

          {/* دکمه‌ها */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-3"
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleShare}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-2xl font-bold shadow-xl hover:shadow-2xl transition-all flex items-center justify-center gap-3"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m9.032 2.684a3 3 0 10-6.064 0m6.064 0a9 9 0 10-18 0 9 9 0 0018 0z" />
              </svg>
              اشتراک‌گذاری لینک
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={downloadQR}
              className="w-full bg-gradient-to-r from-gray-700 to-gray-900 text-white py-4 rounded-2xl font-bold shadow-xl hover:shadow-2xl transition-all flex items-center justify-center gap-3"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              دانلود QR کد
            </motion.button>
          </motion.div>

          {/* توضیحات */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-10 text-center text-xs text-gray-600 bg-white/70 backdrop-blur-sm rounded-2xl p-4 border border-gray-200"
          >
            <p className="mb-1">به ازای هر دوست که با کد شما ثبت‌نام کند و اولین خرید/فروش را انجام دهد،</p>
            <p className="font-bold text-purple-600 text-lg">0.005 گرم طلا هدیه بگیرید!</p>
          </motion.div>
        </div>
      </div>
    </>
  );
}